/**
 * Safely processes email HTML content by:
 * 1. Stripping out JavaScript
 * 2. Scoping CSS to prevent global style pollution
 * 3. Sanitizing potentially dangerous content
 */

export interface SafeEmailResult {
  html: string;
  css: string;
}

export function processEmailHtml(
  htmlContent: string,
  containerId: string = "email-container"
): SafeEmailResult {
  if (!htmlContent) {
    return { html: "", css: "" };
  }

  // Create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;

  // Remove all script tags and their content
  const scripts = tempDiv.querySelectorAll("script");
  scripts.forEach((script) => script.remove());

  // Remove any inline event handlers (onclick, onload, etc.)
  const allElements = tempDiv.querySelectorAll("*");
  allElements.forEach((element) => {
    // Remove all event handler attributes
    const eventAttributes = [
      "onclick",
      "onload",
      "onerror",
      "onmouseover",
      "onmouseout",
      "onfocus",
      "onblur",
      "onchange",
      "onsubmit",
      "onreset",
      "onselect",
      "onkeydown",
      "onkeyup",
      "onkeypress",
      "ondblclick",
      "onmousedown",
      "onmouseup",
      "onmousemove",
    ];

    eventAttributes.forEach((attr) => {
      element.removeAttribute(attr);
    });

    // Remove javascript: URLs from href and src attributes
    const href = element.getAttribute("href");
    if (href && href.toLowerCase().startsWith("javascript:")) {
      element.removeAttribute("href");
    }

    const src = element.getAttribute("src");
    if (src && src.toLowerCase().startsWith("javascript:")) {
      element.removeAttribute("src");
    }
  });

  // Extract and scope CSS
  const css = extractAndScopeCss(tempDiv, containerId);

  // Get the cleaned HTML
  const cleanHtml = tempDiv.innerHTML;

  return {
    html: cleanHtml,
    css: css,
  };
}

function extractAndScopeCss(element: HTMLElement, containerId: string): string {
  const cssRules: string[] = [];

  // Extract CSS from <style> tags
  const styleTags = element.querySelectorAll("style");
  styleTags.forEach((styleTag) => {
    const cssText = styleTag.textContent || "";
    const scopedCss = scopeCssRules(cssText, containerId);
    cssRules.push(scopedCss);
    styleTag.remove(); // Remove the original style tag
  });

  // Extract CSS from style attributes and convert to scoped rules
  const elementsWithStyle = element.querySelectorAll("[style]");
  elementsWithStyle.forEach((el, index) => {
    const inlineStyle = el.getAttribute("style");
    if (inlineStyle) {
      // Create a unique class for this element
      const uniqueClass = `email-inline-${index}`;
      el.classList.add(uniqueClass);

      // Convert inline style to scoped CSS rule
      const scopedRule = `.${containerId} .${uniqueClass} { ${inlineStyle} }`;
      cssRules.push(scopedRule);

      // Remove the inline style attribute
      el.removeAttribute("style");
    }
  });

  return cssRules.join("\n");
}

function scopeCssRules(cssText: string, containerId: string): string {
  // Remove CSS comments first
  let cleanCss = cssText.replace(/\/\*[\s\S]*?\*\//g, "");

  // Track brace depth for nested structures (media queries, keyframes)
  let result = "";
  let i = 0;

  while (i < cleanCss.length) {
    // Find the next opening brace
    const braceIndex = cleanCss.indexOf("{", i);
    if (braceIndex === -1) {
      // No more rules, append the rest
      result += cleanCss.slice(i);
      break;
    }

    // Extract the selector part (everything from current position to the brace)
    const selectorPart = cleanCss.slice(i, braceIndex).trim();

    // Check if this is an at-rule (@media, @keyframes, @font-face, etc.)
    if (selectorPart.startsWith("@")) {
      // For @media and @supports, we need to find the matching closing brace
      // and recursively scope the contents
      if (
        selectorPart.startsWith("@media") ||
        selectorPart.startsWith("@supports")
      ) {
        // Find the matching closing brace
        let depth = 1;
        let endIndex = braceIndex + 1;
        while (depth > 0 && endIndex < cleanCss.length) {
          if (cleanCss[endIndex] === "{") depth++;
          else if (cleanCss[endIndex] === "}") depth--;
          endIndex++;
        }

        // Extract and recursively scope the content inside the media query
        const innerContent = cleanCss.slice(braceIndex + 1, endIndex - 1);
        const scopedInner = scopeCssRules(innerContent, containerId);
        result += `${selectorPart} {\n${scopedInner}\n}`;
        i = endIndex;
      } else {
        // For other at-rules (@keyframes, @font-face), pass through as-is
        let depth = 1;
        let endIndex = braceIndex + 1;
        while (depth > 0 && endIndex < cleanCss.length) {
          if (cleanCss[endIndex] === "{") depth++;
          else if (cleanCss[endIndex] === "}") depth--;
          endIndex++;
        }
        result += cleanCss.slice(i, endIndex);
        i = endIndex;
      }
    } else {
      // Regular CSS rule - find the closing brace
      const closeIndex = cleanCss.indexOf("}", braceIndex);
      if (closeIndex === -1) {
        // Malformed CSS, append the rest
        result += cleanCss.slice(i);
        break;
      }

      // Extract the declaration part
      const declarationPart = cleanCss.slice(braceIndex + 1, closeIndex);

      // Scope each selector
      const scopedSelectors = selectorPart.split(",").map((selector) => {
        const trimmed = selector.trim();
        if (!trimmed) return trimmed;

        // Skip if already scoped or if it's html/body (which we'll ignore)
        if (trimmed.startsWith(`.${containerId}`)) {
          return trimmed;
        }

        // Skip html and body selectors entirely - they shouldn't affect the container
        if (trimmed === "html" || trimmed === "body") {
          return null;
        }

        // For selectors that start with html or body, strip that prefix
        const htmlBodyRegex = /^(html|body)\s+/;
        const strippedSelector = trimmed.replace(htmlBodyRegex, "");

        // Add scoping prefix
        return `.${containerId} ${strippedSelector}`;
      });

      // Filter out null selectors and join
      const filteredSelectors = scopedSelectors.filter(
        (s) => s !== null && s.trim() !== ""
      );

      if (filteredSelectors.length > 0) {
        result += `${filteredSelectors.join(", ")} {${declarationPart}}`;
      }

      i = closeIndex + 1;
    }
  }

  return result;
}

/**
 * Additional utility to strip potentially dangerous HTML tags
 */
export function sanitizeEmailHtml(html: string): string {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Remove potentially dangerous tags
  const dangerousTags = [
    "script",
    "iframe",
    "object",
    "embed",
    "form",
    "input",
    "button",
  ];
  dangerousTags.forEach((tag) => {
    const elements = tempDiv.querySelectorAll(tag);
    elements.forEach((element) => element.remove());
  });

  return tempDiv.innerHTML;
}
